import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, FileText, AlertTriangle, RotateCcw, Save, X, Images, Download, FolderInput, ListOrdered, Hash, FileDown } from 'lucide-react';
import { buildRelazioneTokens } from './ReportEditorView';
import { buildBonificaTokens } from './ReportEditorViewBonifiche';
import { PraticaService, IncaricoService, SettingsService } from '../services/apiService';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { useToast } from '../components/ToastContext';
import { relazioneFileName } from '../utils/relazioneFileName';

/**
 * Editor della relazione in DOCX tramite OnlyOffice Docs (self-hosted).
 *
 * Requisiti:
 * - OnlyOffice Docs raggiungibile su REACT_APP_ONLYOFFICE_URL.
 * - Il BE espone GET /api/relazione-docx/{id}/document e
 * POST /api/relazione-docx/{id}/callback (dove OnlyOffice rimanda il docx salvato).
 * - document.url e callbackUrl li costruisce il BE (editor-token) da app.docx.be.server-url,
 * l'URL del BE raggiungibile dal container OnlyOffice.
 */
// Toglie lo slash finale e un eventuale "/api" finale: i path qui includono già
// "/api/relazione-docx/...", e REACT_APP_API_URL spesso termina con /api.
const normBase = (u: string) => (u || '').replace(/\/$/, '').replace(/\/api$/, '');
const ONLYOFFICE_URL = (process.env.REACT_APP_ONLYOFFICE_URL || '').replace(/\/$/, '');
// Base URL del BE vista dal browser: config del plugin e lista/thumbnail foto.
const API_BROWSER = normBase(process.env.REACT_APP_API_URL || '');

export const RelazioneDocxView: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    // La relazione DOCX è per (pratica, soggetto). Il soggetto arriva da ?soggetto=NOME
    // (vuoto = relazione unica) e viene passato a tutti gli endpoint DOCX, così
    // soggetti diversi non sovrascrivono lo stesso file.
    const [searchParams] = useSearchParams();
    const soggetto = searchParams.get('soggetto') || '';
    const sogQ = soggetto ? `?soggetto=${encodeURIComponent(soggetto)}` : '';
    // Slug per la document.key di OnlyOffice: diverso per soggetto e solo alfanumerico.
    const sogKey = (soggetto.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')) || 'default';
    const containerRef = useRef<HTMLDivElement>(null);
    const editorRef = useRef<any>(null);
    // document.key corrente, serve al forcesave anche da beforeunload, dove non
    // abbiamo la closure di init.
    const docKeyRef = useRef<string>('');
    // Token "pt" (per pratica, a scadenza) richiesto dal BE su /api/relazione-docx/**.
    // Lo rilascia /access-token e va aggiunto agli URL, perché sendBeacon e il
    // plugin non mandano il Bearer.
    const ptRef = useRef<string>('');
    const withPt = (url: string) => ptRef.current
        ? `${url}${url.includes('?') ? '&' : '?'}pt=${encodeURIComponent(ptRef.current)}`
        : url;
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    // Modifiche non salvate (da onDocumentStateChange) per la guardia d'uscita.
    const dirtyRef = useRef(false);
    // Una volta true resta true: lo stato "salvato" di OnlyOffice riguarda il suo
    // store, non per forza il backend, quindi avvisiamo se l'utente ha modificato qualcosa.
    const hasEditedRef = useRef(false);
    const [hasEdited, setHasEdited] = useState(false); // Testo del modale di useUnsavedGuard
    const [busy, setBusy] = useState<string | null>(null);
    const { addToast } = useToast();
    // Avviso non bloccante se la pre-compilazione fallisce (config mancante o
    // conversione fallita), per capire perché si vede il documento di esempio.
    const [precompileWarn, setPrecompileWarn] = useState<string | null>(null);
    // Ogni incremento ricrea l'editor (dopo il Reset riparte dal documento iniziale).
    const [reloadTick, setReloadTick] = useState(0);

    // Genera il DOCX iniziale riempiendo lato BE il template con i dati della pratica
    // (/init-from-template, senza ConvertService). I valori sono calcolati come
    // nell'editor HTML (buildRelazioneTokens). Gli errori vengono loggati e non
    // bloccano l'apertura: il BE serve comunque il documento di esempio.
    const ensureInitialDoc = async (): Promise<void> => {
        // Pratica, incarichi e settings in parallelo, come in ReportEditorView.
        const [praticaRes, incarichiRes, settingsRes] = await Promise.all([
            PraticaService.getById(Number(id)),
            IncaricoService.getByPratica(Number(id), { includeAutogenerati: true }),
            SettingsService.getAll(),
        ]);
        const pData = (praticaRes as any).data || praticaRes;
        const rawIncarichi = (incarichiRes as any).data || incarichiRes;
        const settingsArr = (settingsRes as any).data || [];
        const agencySettings = Array.isArray(settingsArr) ? settingsArr[0] : settingsArr;
        const agencyName = agencySettings?.agencyName || '';
        // Esclude gli incarichi AUTOGENERATO (contenitori di upload tardivi), che falserebbero
        // il calcolo del primo giorno, come nell'editor HTML.
        const incarichiTemplate = (Array.isArray(rawIncarichi) ? rawIncarichi : [])
            .filter((i: any) => i?.tipoOrigine !== 'AUTOGENERATO');

        // La bonifica ('prev-bonifiche') ha template e derivazioni proprie; il resto usa
        // la relazione investigativa. Stessa logica dello switch in DettaglioPraticaView.
        const isBonifica = (pData as any)?.modelId === 'prev-bonifiche';
        // La consulenza ha un template proprio ma usa gli stessi token dell'investigativa
        // (senza soggetto).
        const isConsulenza = (pData as any)?.modelId === 'prev-consulenza';

        // Mappa token → valore + URL del timbro.
        const { tokens, timbroUrl, perContoProprio } = isBonifica
            ? buildBonificaTokens(pData, incarichiTemplate, agencyName, agencySettings?.timbroUrl, agencySettings?.city)
            : buildRelazioneTokens(pData, incarichiTemplate, agencyName, agencySettings?.timbroUrl, agencySettings?.city, soggetto || '');

        // Il BE riempie il template e lo salva come documento iniziale (NUMPAGES diventa
        // campo Word, TIMBRO un'immagine inline). templateType sceglie il template; intestazione e
        // piè di pagina dell'agenzia vengono aggiunti per tutti i tipi.
        const resp = await fetch(withPt(`${API_BROWSER}/api/relazione-docx/${id}/init-from-template${sogQ}`), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                tokens,
                timbroUrl,
                reportHeaderUrl: agencySettings?.reportHeaderUrl || '',
                reportFooterUrl: agencySettings?.reportFooterUrl || '',
                templateType: isBonifica ? 'bonifica' : isConsulenza ? 'consulenza' : 'relazione',
                // Per conto proprio: modello senza la tabella del soggetto per cui si agisce.
                perContoProprio,
            }),
        });
        if (!resp.ok) {
            const txt = await resp.text().catch(() => '');
            // Il BE risponde { ok:false, error:"..." }: estraggo il messaggio per il banner.
            let detail = txt;
            try { const j = JSON.parse(txt); if (j?.error) detail = String(j.error); } catch (_) { /* Testo grezzo */ }
            throw new Error(`HTTP ${resp.status} — ${detail}`);
        }
    };

    useEffect(() => {
        if (!ONLYOFFICE_URL) {
            setError('OnlyOffice non configurato: imposta REACT_APP_ONLYOFFICE_URL (URL del container Docs) e ricompila il web.');
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        setPrecompileWarn(null);
        dirtyRef.current = false;
        const apiSrc = `${ONLYOFFICE_URL}/web-apps/apps/api/documents/api.js`;

        // La key deve cambiare quando cambia il documento salvato, altrimenti OnlyOffice
        // riapre la sua cache. Usiamo la "version" (lastModified lato BE).
        const init = async () => {
            if (cancelled || !containerRef.current) return;
            const DocsAPI = (window as any).DocsAPI;
            if (!DocsAPI) { setError('api.js di OnlyOffice non caricato (URL errato o container irraggiungibile).'); setLoading(false); return; }
            // Senza token il BE risponde 403 a tutti gli endpoint dell'editor.
            ptRef.current = '';
            try {
                const authToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
                const ar = await fetch(`${API_BROWSER}/api/relazione-docx/${id}/access-token`, {
                    headers: {
                        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
                        'X-Client-Source': 'WEB',
                    },
                });
                if (ar.ok) { const aj = await ar.json().catch(() => ({} as any)); ptRef.current = aj?.token || ''; }
                // Fallback per il plugin (stessa origin): se initData non gli passa il token
                // lo legge da qui. Scade da solo ed è firmato per pratica.
                if (ptRef.current) { try { localStorage.setItem(`docx-pt-${id}`, ptRef.current); } catch (_) { /* Storage bloccato */ } }
                // Soggetto della relazione per il pannello Media: OnlyOffice lo perde quando ricarica la
                // config del plugin. Nella sessione della scheda, così due relazioni in due schede non si mescolano.
                try { sessionStorage.setItem(`docx-sog-${id}`, soggetto); } catch (_) { /* Storage bloccato */ }
            } catch (_) { /* Gestito sotto: senza token non si apre */ }
            if (cancelled) return;
            if (!ptRef.current) {
                setError('Accesso all\'editor non autorizzato o sessione scaduta: rientra e riprova.');
                setLoading(false);
                return;
            }
            let version = 0;
            try {
                const r = await fetch(withPt(`${API_BROWSER}/api/relazione-docx/${id}/version${sogQ}`));
                if (r.ok) { const j = await r.json(); version = Number(j?.version) || 0; }
            } catch (_) { /* Senza version la key resta quella base */ }
            if (cancelled) return;

            // Se non c'è ancora un documento salvato (version 0, prima apertura o dopo
            // un Reset) genera il DOCX pre-compilato e rilegge la version per la docKey.
            // Se fallisce si apre comunque l'editor con il documento di esempio.
            if (version === 0) {
                setBusy('Preparo la relazione…');
                try {
                    await ensureInitialDoc();
                    if (cancelled) return;
                    const r2 = await fetch(withPt(`${API_BROWSER}/api/relazione-docx/${id}/version${sogQ}`));
                    if (cancelled) return;
                    if (r2.ok) { const j2 = await r2.json(); version = Number(j2?.version) || 0; }
                } catch (e: any) {
                    if (cancelled) return;
                    const msg = String(e?.message || e);
                    // eslint-disable-next-line no-console
                    console.warn('[RelazioneDocx] pre-compilazione fallita, apro comunque l\'editor:', e);
                    // Caso più comune: manca la config internal-url (400).
                    if (/internal-url/i.test(msg) || /HTTP 400/.test(msg)) {
                        setPrecompileWarn('Pre-compilazione non attiva: manca la configurazione del document server OnlyOffice lato BE (app.docx.onlyoffice.internal-url). L\'editor apre il documento vuoto. Dettaglio: ' + msg);
                    } else {
                        setPrecompileWarn('Pre-compilazione non riuscita, apro il documento vuoto. Dettaglio: ' + msg);
                    }
                } finally {
                    if (!cancelled) setBusy(null);
                }
            }

            // La key vera la decide il BE nella config; questa serve solo finché non arriva.
            docKeyRef.current = `relazione-${id}-${sogKey}-v${version}`;

            // Nome del file scaricato: Relazione_<id>[_<n soggetto>].docx, senza nomi.
            let docTitle = relazioneFileName(id, null, null, 'docx');
            try {
                const pr: any = await PraticaService.getById(Number(id));
                if (cancelled) return;
                docTitle = relazioneFileName(id, pr?.data || pr, soggetto, 'docx');
            } catch (_) { /* Resta il titolo di default */ }
            if (cancelled) return;

            try {
                // La config del document server (documentType/document/editorConfig: URL del
                // documento, callback, key, permessi, plugin) la costruisce e firma il BE per
                // questa pratica; da qui passano solo il titolo e la base del BE vista dal browser.
                // width/height/events restano lato browser e non vanno firmati.
                let signPayload: any = null;
                let ooToken: string | null = null;
                try {
                    const authToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
                    const tr = await fetch(`${API_BROWSER}/api/relazione-docx/${id}/editor-token${sogQ}`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
                            'X-Client-Source': 'WEB',
                        },
                        body: JSON.stringify({ title: docTitle, browserBase: API_BROWSER }),
                    });
                    if (tr.ok) {
                        const tj = await tr.json().catch(() => ({} as any));
                        if (tj?.config) signPayload = tj.config;
                        if (tj?.token) ooToken = tj.token;
                    }
                } catch (_) { /* Gestito sotto: senza config non si apre */ }
                if (cancelled) return;
                if (!signPayload) {
                    setError('Configurazione dell\'editor non disponibile: rientra e riprova.');
                    setLoading(false);
                    return;
                }
                if (signPayload?.document?.key) docKeyRef.current = signPayload.document.key;
                const cfg: any = {
                    ...signPayload,
                    width: '100%',
                    height: '100%',
                    events: {
                        onAppReady: () => { if (!cancelled) setLoading(false); },
                        // true = modifiche non salvate, false = tutto salvato.
                        onDocumentStateChange: (e: any) => {
                            dirtyRef.current = !!(e && e.data);
                            if (e && e.data) { hasEditedRef.current = true; setHasEdited(true); } // Una volta modificato resta true
                        },
                        onError: (e: any) => { if (!cancelled) { setError('Errore OnlyOffice: ' + (e?.data || 'sconosciuto')); setLoading(false); } },
                    },
                };
                if (ooToken) cfg.token = ooToken;
                editorRef.current = new DocsAPI.DocEditor(containerRef.current.id, cfg);
            } catch (e: any) {
                setError('Init OnlyOffice fallito: ' + (e?.message || e));
                setLoading(false);
            }
        };

        // Carica api.js una sola volta
        const existing = document.querySelector(`script[src="${apiSrc}"]`) as HTMLScriptElement | null;
        if ((window as any).DocsAPI) { init(); }
        else if (existing) { existing.addEventListener('load', init); }
        else {
            const s = document.createElement('script');
            s.src = apiSrc; s.async = true;
            s.onload = init;
            s.onerror = () => { setError('Impossibile caricare api.js da ' + apiSrc + ' (container OnlyOffice raggiungibile dal browser?).'); setLoading(false); };
            document.body.appendChild(s);
        }

        return () => {
            cancelled = true;
            try { editorRef.current?.destroyEditor?.(); } catch (_) { /* noop */ }
        };
    }, [id, reloadTick, soggetto]);

    // Forcesave: chiede al backend di farsi consegnare subito le modifiche da
    // OnlyOffice (CommandService, status 6). OnlyOffice le invia solo alla
    // disconnessione o dopo inattività, quindi senza questo un refresh le perderebbe.
    const doForceSave = (useBeacon: boolean) => {
        const key = docKeyRef.current;
        if (!key) return;
        // Key come query param, così sendBeacon fa una richiesta semplice senza preflight CORS.
        const url = withPt(`${API_BROWSER}/api/relazione-docx/${id}/forcesave?key=${encodeURIComponent(key)}`);
        try {
            if (useBeacon && navigator.sendBeacon) {
                navigator.sendBeacon(url); // Sopravvive all'unload, quindi arriva anche al refresh
            } else {
                fetch(url, { method: 'POST', keepalive: true }).catch(() => { /* best-effort */ });
            }
        } catch (_) { /* best-effort */ }
    };

    // "Salva ed esci": forcesave, distruzione dell'editor (anche il disconnect
    // salva) e breve attesa.
    const doSaveNow = async (): Promise<boolean> => {
        doForceSave(false);
        try { editorRef.current?.destroyEditor?.(); } catch (_) { /* noop */ }
        await new Promise((r) => setTimeout(r, 600));
        return true;
    };

    // Guardia d'uscita sempre attiva, come nell'editor HTML: avvisa su F5/chiusura,
    // link interni e tasto indietro, perché il "salvato" di OnlyOffice non garantisce
    // la persistenza sul backend.
    const { requestBack, modal: unsavedModal } = useUnsavedGuard({
        isDirty: hasEdited,
        lastSaved: null,
        onSaveNow: doSaveNow,
        fallbackPath: `/pratiche/${id}`,
        backPath: `/pratiche/${id}`,
    });

    // Flush verso il backend prima dell'unload, oltre all'avviso dell'hook.
    useEffect(() => {
        const flush = () => doForceSave(true);
        window.addEventListener('beforeunload', flush);
        return () => window.removeEventListener('beforeunload', flush);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reset: elimina la versione salvata e ricrea l'editor dal documento iniziale.
    const doReset = async () => {
        if (!window.confirm('Reset della relazione: verranno annullate le modifiche salvate e si riparte dal documento iniziale pre-compilato. Procedere?')) return;
        setBusy('Reset in corso…');
        try {
            const rr = await fetch(withPt(`${API_BROWSER}/api/relazione-docx/${id}/reset${sogQ}`), { method: 'POST' });
            if (!rr.ok) throw new Error(`HTTP ${rr.status}`);
            dirtyRef.current = false;
            try { editorRef.current?.destroyEditor?.(); } catch (_) { /* noop */ }
            editorRef.current = null;
            setReloadTick(t => t + 1); // Riesegue l'effect: version 0 → nuova key → documento iniziale
        } catch (e: any) {
            setError('Reset fallito: ' + (e?.message || e));
        } finally {
            setBusy(null);
        }
    };

    // L'URL presigned è su un altro dominio, quindi l'attributo download viene ignorato e un
    // <a> farebbe navigare la pagina: scatterebbero l'avviso d'uscita e il forcesave di unload.
    // In un iframe nascosto il Content-Disposition "attachment" scarica senza lasciare la pagina.
    const scaricaInIframe = (url: string) => {
        const f = document.createElement('iframe');
        f.style.display = 'none';
        f.src = url;
        document.body.appendChild(f);
        setTimeout(() => f.remove(), 60000);
    };

    // PDF della relazione: il BE fa il forcesave con la key corrente, attende la
    // nuova versione e la converte in PDF.
    // - archivia=false → "Scarica PDF": solo download.
    // - archivia=true  → "Salva in documentazione": archiviato in Documentazione
    // ufficio del mandato al posto del precedente, senza download.
    // Il download dal menu interno di OnlyOffice non passa da qui.
    const generaPdf = async (archivia: boolean) => {
        setBusy(archivia ? 'Salvo il PDF in Documentazione…' : 'Genero il PDF…');
        try {
            const authToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
            const params = new URLSearchParams();
            if (soggetto) params.set('soggetto', soggetto);
            if (docKeyRef.current) params.set('key', docKeyRef.current);
            params.set('archivia', String(archivia));
            const r = await fetch(withPt(`${API_BROWSER}/api/relazione-docx/${id}/pdf?${params.toString()}`), {
                method: 'POST',
                headers: {
                    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
                    'X-Client-Source': 'WEB',
                },
            });
            const j: any = await r.json().catch(() => ({}));
            if (!r.ok || !j?.url) {
                const detail = j?.error
                    || (r.status === 401 || r.status === 403 ? 'sessione scaduta o accesso non autorizzato' : `HTTP ${r.status}`);
                throw new Error(String(detail));
            }
            if (archivia) {
                addToast('PDF salvato in Documentazione ufficio del mandato', 'success');
            } else {
                scaricaInIframe(j.url);
                addToast('PDF scaricato', 'success');
            }
            if (j.aggiornato === false) {
                addToast('Attenzione: le ultime modifiche potrebbero non essere incluse (salvataggio non confermato in tempo). Riprova tra qualche secondo.', 'info');
            }
        } catch (e: any) {
            const msg = String(e?.message || e);
            addToast(/failed to fetch|networkerror/i.test(msg)
                ? 'PDF non generato: server non raggiungibile.'
                : `PDF non generato: ${msg}`, 'error');
        } finally {
            setBusy(null);
        }
    };

    // "Scarica Word": il BE fa il forcesave, rimette le foto in versione piena (nell'editor
    // sono leggere) e le didascalie in sequenza. Il download dal menu di OnlyOffice consegna
    // invece il documento com'è nell'editor, con le foto leggere.
    const scaricaWord = async () => {
        setBusy('Preparo il documento Word…');
        try {
            const authToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
            const params = new URLSearchParams();
            if (soggetto) params.set('soggetto', soggetto);
            if (docKeyRef.current) params.set('key', docKeyRef.current);
            const r = await fetch(withPt(`${API_BROWSER}/api/relazione-docx/${id}/docx?${params.toString()}`), {
                method: 'POST',
                headers: {
                    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
                    'X-Client-Source': 'WEB',
                },
            });
            const j: any = await r.json().catch(() => ({}));
            if (!r.ok || !j?.url) {
                const detail = j?.error
                    || (r.status === 401 || r.status === 403 ? 'sessione scaduta o accesso non autorizzato' : `HTTP ${r.status}`);
                throw new Error(String(detail));
            }
            scaricaInIframe(j.url);
            addToast('Documento Word scaricato', 'success');
            if (j.aggiornato === false) {
                addToast('Attenzione: le ultime modifiche potrebbero non essere incluse (salvataggio non confermato in tempo). Riprova tra qualche secondo.', 'info');
            }
        } catch (e: any) {
            const msg = String(e?.message || e);
            addToast(/failed to fetch|networkerror/i.test(msg)
                ? 'Documento Word non generato: server non raggiungibile.'
                : `Documento Word non generato: ${msg}`, 'error');
        } finally {
            setBusy(null);
        }
    };

    // Riapre il pannello "Media Mandato" se chiuso con la X: OnlyOffice non offre
    // un modo per riaprirlo, quindi ricreiamo l'editor (il plugin parte in autostart).
    // Autosave e disconnect salvano prima, quindi non si perde nulla.
    const reopenPanel = () => {
        try { editorRef.current?.destroyEditor?.(); } catch (_) { /* noop */ }
        editorRef.current = null;
        setReloadTick(t => t + 1); // Il documento salvato non cambia: version > 0, nessuna rigenerazione
    };

    // Comandi al plugin "Media Mandato" (stessa origin): BroadcastChannel e, in alternativa,
    // localStorage. Il plugin risponde su 'mm-plugin-res'. Serve il pannello foto aperto.
    const comandoPlugin = (action: 'rinumera' | 'contaPagine', etichetta: string) => {
        const cmdId = `${action}-${Date.now()}`;
        const msg = { id: cmdId, action, praticaId: id };
        let finito = false;
        let bc: BroadcastChannel | null = null;
        let poll: ReturnType<typeof setInterval> | undefined;
        let scadenza: ReturnType<typeof setTimeout> | undefined;
        const chiudi = () => {
            finito = true;
            try { bc?.close(); } catch (_) { /* noop */ }
            if (poll) clearInterval(poll);
            if (scadenza) clearTimeout(scadenza);
            try { localStorage.removeItem('mm-plugin-cmd'); } catch (_) { /* noop */ }
            setBusy(null);
        };
        const esito = (r: { id?: string; ok?: boolean; allegati?: number; pagine?: number; sostituiti?: number; campi?: number } | null) => {
            if (finito || !r || r.id !== cmdId) return;
            chiudi();
            if (!r.ok) { addToast(`${etichetta}: operazione non riuscita, riprova`, 'error'); return; }
            if (action === 'rinumera') {
                addToast(r.allegati ? `Allegati rinumerati: ${r.allegati}` : 'Nessuna didascalia «Allegato n.» nel documento', 'success');
            } else if (r.sostituiti || r.campi) {
                const punti = (r.campi || 0) + (r.sostituiti || 0);
                addToast(`Pagine: ${r.pagine} — numero aggiornato in ${punti} punt${punti === 1 ? 'o' : 'i'} del documento`, 'success');
            } else {
                addToast(`Pagine: ${r.pagine} — scrivi «- - - XXX - - -» dove vuoi il numero di pagine e ripremi`, 'info');
            }
        };
        setBusy(action === 'contaPagine' ? 'Sto contando le pagine…' : `${etichetta}…`);
        try { bc = new BroadcastChannel('mm-plugin-res'); bc.onmessage = (e) => esito(e.data); } catch (_) { /* noop */ }
        poll = setInterval(() => {
            try { const s = localStorage.getItem('mm-plugin-res'); if (s) esito(JSON.parse(s)); } catch (_) { /* noop */ }
        }, 300);
        // Il conteggio aspetta che l'editor abbia impaginato tutto il documento: subito dopo
        // l'apertura di una relazione lunga può volerci molto più della rinumerazione.
        scadenza = setTimeout(() => {
            if (finito) return;
            chiudi();
            addToast(action === 'contaPagine'
                ? 'Il documento è ancora in caricamento: attendi qualche secondo e riprova'
                : 'Il pannello foto non risponde: premi Aggiorna per riaprirlo e riprova', 'error');
        }, action === 'contaPagine' ? 60000 : 15000);
        try { const c = new BroadcastChannel('mm-plugin-cmd'); c.postMessage(msg); c.close(); } catch (_) { /* noop */ }
        try { localStorage.setItem('mm-plugin-cmd', JSON.stringify(msg)); } catch (_) { /* noop */ }
    };

    return (
        <div className="flex flex-col h-screen bg-gray-100">
            <div className="flex items-center gap-2 px-3 py-1 bg-white border-b border-gray-200 shrink-0">
                <button onClick={requestBack} className="px-2 py-1 text-gray-600 hover:bg-gray-100 rounded-md flex items-center gap-1 text-xs font-bold">
                    <ChevronLeft size={14}/> Indietro
                </button>
                <div className="flex items-center gap-1.5 text-sm text-indigo-700 font-bold">
                    <FileText size={14}/> Relazione DOCX 
                </div>
                <div className="ml-auto flex items-center gap-1">
                    {loading && !error && <span className="text-xs text-gray-400">Caricamento editor…</span>}
                    {!error && (
                        <button onClick={reopenPanel} disabled={!!busy}
                            className="px-2 py-1 text-gray-600 hover:bg-gray-100 rounded-md flex items-center gap-1 text-xs font-bold disabled:opacity-50"
                            title="Riapri il pannello foto (Media Mandato) se l'hai chiuso con la X">
                            <Images size={13}/> Aggiorna
                        </button>
                    )}
                    {!error && (
                        <button onClick={() => comandoPlugin('rinumera', 'Rinumera allegati')} disabled={!!busy || loading}
                            className="px-2 py-1 text-gray-600 hover:bg-gray-100 rounded-md flex items-center gap-1 text-xs font-bold disabled:opacity-50"
                            title="Rinumera le didascalie «Allegato n.» nell'ordine del documento (dopo aver cancellato o spostato una foto)">
                            <ListOrdered size={13}/> Rinumera allegati
                        </button>
                    )}
                    {!error && (
                        <button onClick={() => comandoPlugin('contaPagine', 'Conta pagine')} disabled={!!busy || loading}
                            className="px-2 py-1 text-gray-600 hover:bg-gray-100 rounded-md flex items-center gap-1 text-xs font-bold disabled:opacity-50"
                            title="Scrive il numero di pagine in «- - - XXX - - -»; ripremendolo il numero tra i trattini si aggiorna">
                            <Hash size={13}/> Conta pagine
                        </button>
                    )}
                    {!error && (
                        <button onClick={() => generaPdf(false)} disabled={!!busy || loading}
                            className="px-2 py-1 text-gray-600 hover:bg-gray-100 rounded-md flex items-center gap-1 text-xs font-bold disabled:opacity-50"
                            title="Scarica la relazione in PDF sul computer">
                            <Download size={13}/> Scarica PDF
                        </button>
                    )}
                    {!error && (
                        <button onClick={scaricaWord} disabled={!!busy || loading}
                            className="px-2 py-1 text-gray-600 hover:bg-gray-100 rounded-md flex items-center gap-1 text-xs font-bold disabled:opacity-50"
                            title="Scarica la relazione in Word con le foto in qualità piena">
                            <FileDown size={13}/> Scarica Word
                        </button>
                    )}
                    {!error && (
                        <button onClick={() => generaPdf(true)} disabled={!!busy || loading}
                            className="px-2 py-1 text-gray-600 hover:bg-gray-100 rounded-md flex items-center gap-1 text-xs font-bold disabled:opacity-50"
                            title="Genera il PDF e salvalo in Documentazione ufficio del mandato (sostituisce la versione precedente)">
                            <FolderInput size={13}/> Salva in documentazione
                        </button>
                    )}
                    {!error && (
                        <button onClick={doReset} disabled={!!busy}
                            className="px-2 py-1 text-gray-600 hover:bg-gray-100 rounded-md flex items-center gap-1 text-xs font-bold disabled:opacity-50"
                            title="Ripristina il documento iniziale pre-compilato (annulla le modifiche salvate)">
                            <RotateCcw size={13}/> Reset
                        </button>
                    )}
                </div>
            </div>
            {precompileWarn && !error && (
                <div className="shrink-0 flex items-start gap-2 px-4 py-2 bg-amber-50 border-b border-amber-300 text-amber-900 text-xs">
                    <AlertTriangle size={15} className="mt-0.5 shrink-0"/>
                    <span className="flex-1">{precompileWarn}</span>
                    <button onClick={() => setPrecompileWarn(null)} className="p-0.5 hover:bg-amber-100 rounded"><X size={14}/></button>
                </div>
            )}
            {error ? (
                <div className="flex-1 flex items-center justify-center p-6">
                    <div className="max-w-lg bg-amber-50 border border-amber-300 rounded-xl p-5 text-amber-900">
                        <div className="flex items-center gap-2 font-bold mb-2"><AlertTriangle size={18}/> Editor DOCX non disponibile</div>
                        <p className="text-sm">{error}</p>
                        <p className="text-xs text-amber-700/80 mt-3">Questo è un POC: serve il container OnlyOffice Docs in esecuzione e REACT_APP_ONLYOFFICE_URL impostata (lato BE app.docx.be.server-url).</p>
                    </div>
                </div>
            ) : (
                <div ref={containerRef} id="onlyoffice-editor" className="flex-1" />
            )}

            {busy && (
                <div className="fixed inset-0 z-50 bg-white/60 flex items-center justify-center">
                    <div className="bg-white border border-gray-200 rounded-xl px-5 py-3 shadow-lg text-sm font-bold text-indigo-700">{busy}</div>
                </div>
            )}

            {/* Modale "modifiche non salvate" della guardia d'uscita. */}
            {unsavedModal}
        </div>
    );
};

export default RelazioneDocxView;
